import { useState, useEffect } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import { Users, RefreshCw, CheckCircle, XCircle, Clock, Shield, TrendingUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/DataTable'
import { Customer } from '@/types'
import { customerService } from '@/services/customerService'
import { formatDistanceToNow } from 'date-fns'
import { TableColumn, TableAction, TablePagination, TableSort, TableFilter } from '@/types/table'

const kycStatusConfig = {
  verified: {
    label: 'Verified',
    color: 'text-pexi-blue bg-pexi-blue/20 border-pexi-blue/30',
    icon: CheckCircle,
  },
  pending: {
    label: 'Pending',
    color: 'text-pexi-orange bg-pexi-orange/20 border-pexi-orange/30',
    icon: Clock,
  },
  rejected: {
    label: 'Rejected',
    color: 'text-gray-400 bg-gray-500/20 border-gray-500/30',
    icon: XCircle,
  },
}

export default function Customers() {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [pagination, setPagination] = useState<TablePagination>({
    current: 1,
    pageSize: 10,
    total: 0,
    pageSizeOptions: [5, 10, 20, 50],
  })
  const [sort, setSort] = useState<TableSort | undefined>({
    key: 'created_at',
    direction: 'desc',
  })
  const [filters, setFilters] = useState<TableFilter[]>([])

  const fetchCustomers = async (params: {
    page: number
    pageSize: number
    sort?: TableSort
    filters: TableFilter[]
    search?: string
  }) => {
    setLoading(true)
    setError(null)
    try {
      const response = await customerService.getCustomers({
        page: params.page,
        limit: params.pageSize,
        search: params.search,
      })

      if (response.success) {
        const { results = [], count = 0 } = response.data
        setCustomers(results)
        setTotalCount(count)
        setPagination(prev => ({
          ...prev,
          current: params.page,
          total: count,
        }))
        return { data: results, total: count }
      } else {
        const errorMessage = response.message || 'Failed to fetch customers'
        setError(errorMessage)
        return { data: [], total: 0 }
      }
    } catch (error: any) {
      const errorMessage = customerService.handleError(error)
      console.error('Failed to fetch customers:', error)
      setError(errorMessage)
      setCustomers([])
      setTotalCount(0)
      setPagination(prev => ({ ...prev, total: 0 }))
      return { data: [], total: 0 }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomers({
      page: pagination.current,
      pageSize: pagination.pageSize,
      sort,
      filters,
      search: '',
    })
  }, [])

  const getKycStatus = (verified: boolean) => {
    return verified ? kycStatusConfig.verified : kycStatusConfig.pending
  }

  // Define table columns
  const columns: TableColumn<Customer>[] = [
    {
      key: 'full_name',
      title: 'Customer',
      dataIndex: 'full_name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (_, record) => (
        <div>
          <p className="font-medium text-white">{record.full_name}</p>
          <p className="text-sm text-gray-300">{record.email || record.phone || 'N/A'}</p>
        </div>
      ),
    },
    {
      key: 'kyc_verified',
      title: 'KYC Status',
      dataIndex: 'kyc_verified',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Verified', value: 'true' },
        { label: 'Pending', value: 'false' },
      ],
      render: (value, record) => {
        const config = getKycStatus(value)
        const StatusIcon = config.icon
        return (
          <div>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}
            >
              <StatusIcon className="w-3 h-3 mr-1" />
              {config.label}
            </span>
            {record.kyc_verified && record.kyc_verified_at && (
              <p className="mt-1 text-xs text-gray-300">
                Verified{' '}
                {formatDistanceToNow(new Date(record.kyc_verified_at), { addSuffix: true })}
              </p>
            )}
          </div>
        )
      },
    },
    {
      key: 'country_of_residence',
      title: 'Location',
      dataIndex: 'country_of_residence',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'United States', value: 'US' },
        { label: 'United Kingdom', value: 'GB' },
        { label: 'Canada', value: 'CA' },
        { label: 'Australia', value: 'AU' },
        { label: 'Kenya', value: 'KE' },
        { label: 'Nigeria', value: 'NG' },
        { label: 'South Africa', value: 'ZA' },
        { label: 'Uganda', value: 'UG' },
        { label: 'Tanzania', value: 'TZ' },
      ],
      render: (_, record) => (
        <div>
          <p className="text-sm text-white">{record.country_of_residence || 'N/A'}</p>
          <p className="text-xs text-gray-300">{record.state_of_residence || 'N/A'}</p>
        </div>
      ),
    },
    {
      key: 'billing_address',
      title: 'Billing Address',
      dataIndex: 'card_street',
      sortable: false,
      filterable: false,
      render: (_, record) => (
        <div>
          <p className="text-sm text-white">{record.card_street || 'N/A'}</p>
          <p className="text-xs text-gray-300">
            {[
              record.card_city,
              record.card_state_code,
              record.card_post_code,
              record.card_country_code,
            ]
              .filter(Boolean)
              .join(', ') || 'N/A'}
          </p>
        </div>
      ),
    },
    {
      key: 'created_at',
      title: 'Joined',
      dataIndex: 'created_at',
      sortable: true,
      filterable: false,
      render: value => (
        <div>
          <p className="text-sm text-white">{new Date(value).toLocaleDateString()}</p>
          <p className="text-xs text-gray-300">
            {formatDistanceToNow(new Date(value), { addSuffix: true })}
          </p>
        </div>
      ),
    },
  ]

  // Define table actions
  const actions: TableAction<Customer>[] = [
    // {
    //   key: 'view',
    //   label: 'View',
    //   icon: <Eye className="w-4 h-4" />,
    //   onClick: record => {
    //     window.location.href = `/customers/${record.id}`
    //   },
    // },
    // {
    //   key: 'edit',
    //   label: 'Edit',
    //   icon: <Edit className="w-4 h-4" />,
    //   onClick: record => {
    //     window.location.href = `/customers/${record.id}/edit`
    //   },
    // },
    // {
    //   key: 'more',
    //   label: '',
    //   icon: <MoreHorizontal className="w-4 h-4" />,
    //   onClick: record => {
    //     console.log('More actions for:', record)
    //   },
    // },
  ]

  return (
    <div
      className="min-h-screen"
      style={pageStyles.container}
    >
      <div className="relative z-10 p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:mb-8">
          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}
          >
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p
                    className="text-sm font-semibold tracking-wide uppercase"
                    style={pageStyles.textSecondary}
                  >
                    Total Customers
                  </p>
                  <p
                    className="mt-2 text-3xl font-bold"
                    style={pageStyles.text}
                  >
                    {totalCount.toLocaleString()}
                  </p>
                  <p
                    className="mt-1 text-xs"
                    style={pageStyles.textSecondary}
                  >
                    All registered users
                  </p>
                </div>
                <div
                  className="p-4 shadow-lg rounded-2xl"
                  style={{ backgroundColor: statusColors.info.backgroundColor }}
                >
                  <Users className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}
          >
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p
                    className="text-sm font-semibold tracking-wide uppercase"
                    style={pageStyles.textSecondary}
                  >
                    KYC Verified
                  </p>
                  <p
                    className="mt-2 text-3xl font-bold"
                    style={pageStyles.text}
                  >
                    {customers.filter(c => c.kyc_verified).length}
                  </p>
                  <p
                    className="mt-1 text-xs"
                    style={pageStyles.textSecondary}
                  >
                    Identity verified
                  </p>
                </div>
                <div
                  className="p-4 shadow-lg rounded-2xl"
                  style={{ backgroundColor: statusColors.info.backgroundColor }}
                >
                  <Shield className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide text-gray-300 uppercase">
                    Pending KYC
                  </p>
                  <p className="mt-2 text-3xl font-bold text-white" style={pageStyles.text}>
                    {customers.filter(c => !c.kyc_verified).length}
                  </p>
                  <p className="mt-1 text-xs text-gray-400" style={pageStyles.textSecondary}>Awaiting verification</p>
                </div>
                <div className="p-4 bg-pexi-orange shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.warning.backgroundColor }}>
                  <Clock className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide text-gray-300 uppercase">
                    New This Month
                  </p>
                  <p className="mt-2 text-3xl font-bold text-white" style={pageStyles.text}>
                    {
                      customers.filter(c => {
                        const createdDate = new Date(c.created_at)
                        const now = new Date()
                        const monthAgo = new Date(
                          now.getFullYear(),
                          now.getMonth() - 1,
                          now.getDate()
                        )
                        return createdDate >= monthAgo
                      }).length
                    }
                  </p>
                  <p className="mt-1 text-xs text-gray-400" style={pageStyles.textSecondary}>Recent signups</p>
                </div>
                <div className="p-4 bg-pexi-blue shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.info.backgroundColor }}>
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-6 mb-8 transition-all duration-300 border card bg-slate-800/80 backdrop-blur-sm border-red-500/30" style={{ ...cardStyles, borderColor: statusColors.error.borderColor }}>
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-pexi-orange/20 rounded-xl">
                <XCircle className="w-6 h-6 text-pexi-orange" style={{ color: statusColors.error.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mb-1 text-lg font-semibold text-white" style={pageStyles.text}>Error loading customers</h3>
                <p className="mb-4 text-sm text-pexi-orange" style={pageStyles.textSecondary}>{error}</p>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      fetchCustomers({
                        page: 1,
                        pageSize: pagination.pageSize,
                        sort,
                        filters,
                        search: '',
                      })
                    }
                    className="text-white bg-pexi-orange/20 border-pexi-orange/30 hover:bg-pexi-orange/30" style={{ backgroundColor: statusColors.warning.backgroundColor, borderColor: statusColors.warning.borderColor }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setError(null)}
                    className="text-pexi-orange hover:text-white hover:bg-pexi-orange/20" style={{ color: statusColors.warning.color }}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
          <DataTable
            columns={columns}
            data={customers}
            loading={loading}
            pagination={pagination}
            filters={filters}
            sort={sort}
            actions={actions}
            rowKey="id"
            onSort={setSort}
            onFilter={setFilters}
            onPageChange={(page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }))
              fetchCustomers({
                page,
                pageSize,
                sort,
                filters,
                search: '',
              })
            }}
            onSearch={search => {
              fetchCustomers({
                page: 1,
                pageSize: pagination.pageSize,
                sort,
                filters,
                search,
              })
            }}
            onServerSideChange={fetchCustomers}
            serverSide={true}
            searchable={true}
            searchPlaceholder="Search customers..."
            emptyText="No customers found"
            emptyIcon={<Users className="w-12 h-12 text-gray-300" style={{ color: statusColors.info.color }} />}
            loadingText="Loading customers..."
            showStats={true}
            showSearch={true}
            showFilters={true}
            showPagination={true}
            showActions={true}
            striped={true}
            hoverable={true}
            bordered={false}
            className="!bg-transparent !border-none !rounded-none"
          />
        </div>
      </div>
    </div>
  )
}
